import React, { useState, useEffect } from 'react';
import policiesData from '../../data/policies/policies.json';

export type PolicyType =
  | 'terms_of_use'
  | 'privacy_policy'
  | 'cookie_policy'
  | 'gdpr_compliance'
  | 'antifraud_policy';

interface PolicyViewerProps {
  policyType: PolicyType;
  language?: 'tr' | 'en';
  fullScreen?: boolean;
  onClose?: () => void;
}

const policyTitles = {
  terms_of_use: {
    tr: 'Kullanım Şartları',
    en: 'Terms of Use'
  },
  privacy_policy: {
    tr: 'Gizlilik Politikası',
    en: 'Privacy Policy'
  },
  cookie_policy: {
    tr: 'Çerez Politikası',
    en: 'Cookie Policy'
  },
  gdpr_compliance: {
    tr: 'GDPR Uyum Politikası',
    en: 'GDPR Compliance Policy'
  },
  antifraud_policy: {
    tr: 'Anti-Fraud Politikası',
    en: 'Anti-Fraud Policy'
  }
};

export const PolicyViewer: React.FC<PolicyViewerProps> = ({
  policyType,
  language = 'tr',
  fullScreen = false,
  onClose
}) => {
  const [currentLang, setCurrentLang] = useState(language);
  const [policyContent, setPolicyContent] = useState<string>('');

  useEffect(() => {
    const content = (policiesData as any)[policyType]?.[currentLang] || '';
    setPolicyContent(content);
  }, [policyType, currentLang]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const blob = new Blob([policyContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${policyType}_${currentLang}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatContent = (content: string) => {
    // Split content into paragraphs and format
    const paragraphs = content.split('\n\n');
    return paragraphs.map((para, index) => {
      // Check if it's a heading (starts with number or is in uppercase)
      const isHeading = /^[0-9]+\./.test(para) || /^[A-Z\s]+$/.test(para.substring(0, 50));

      if (isHeading) {
        return (
          <h3
            key={index}
            className="text-xl font-semibold mt-6 mb-4 text-gray-900"
          >
            {para}
          </h3>
        );
      }

      // Handle list items
      if (para.startsWith('- ') || para.startsWith('• ')) {
        const items = para.split(/\n(?=[-•])/);
        return (
          <ul key={index} className="list-disc pl-6 my-4 space-y-1">
            {items.map((item, idx) => (
              <li key={idx} className="text-gray-700">{item.replace(/^[-•]\s*/, '')}</li>
            ))}
          </ul>
        );
      }

      return (
        <p
          key={index}
          className="mb-4 text-justify leading-relaxed text-gray-700"
        >
          {para}
        </p>
      );
    });
  };

  const content = (
    <>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          {policyTitles[policyType][currentLang]}
        </h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentLang(currentLang === 'tr' ? 'en' : 'tr')}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
            </svg>
            {currentLang === 'tr' ? 'English' : 'Türkçe'}
          </button>
          <button
            onClick={handlePrint}
            className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
            title="Yazdır"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
          </button>
          <button
            onClick={handleDownload}
            className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
            title="İndir"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <hr className="mb-6 border-gray-200" />

      <div className={`overflow-auto ${fullScreen ? 'max-h-[calc(100vh-200px)]' : 'max-h-[600px]'}`}>
        {formatContent(policyContent)}
      </div>
    </>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] mx-4">
          <div className="p-6 overflow-hidden">
            {content}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {content}
    </div>
  );
};